import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { createInspection } from '../../lib/apiClient';
import type { Property, ReportPreset } from '../../types';

interface CreateInspectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  properties: Property[];
  presets: ReportPreset[];
  isLoadingProperties?: boolean;
  onCreated?: (inspectionId: string) => void;
  onRequestCreateProperty?: () => void;
}

interface FormState {
  propertyId: string;
  scheduledFor: string;
  reportPresetId: string;
}

const initialFormState: FormState = {
  propertyId: '',
  scheduledFor: '',
  reportPresetId: '',
};

export function CreateInspectionDialog({
  isOpen,
  onClose,
  properties,
  presets,
  isLoadingProperties = false,
  onCreated,
  onRequestCreateProperty,
}: CreateInspectionDialogProps): JSX.Element | null {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const hasProperties = properties.length > 0;

  useEffect(() => {
    if (isOpen) {
      setError(null);
      const defaultProperty = properties[0]?._id ?? '';
      const defaultPreset = presets.find((preset) => preset.isDefault)?._id ?? '';
      setForm({
        propertyId: defaultProperty,
        scheduledFor: '',
        reportPresetId: defaultPreset,
      });
    }
  }, [isOpen, properties, presets]);

  const mutation = useMutation({
    mutationFn: createInspection,
    onSuccess: async (inspection) => {
      await queryClient.invalidateQueries({ queryKey: ['inspections'] });
      onCreated?.(inspection._id);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to create inspection');
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setForm(initialFormState);
      setError(null);
      mutation.reset();
    }
  }, [isOpen, mutation]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.propertyId) {
      setError('Select a property to continue');
      return;
    }
    setError(null);
    mutation.mutate({
      propertyId: form.propertyId,
      scheduledFor: form.scheduledFor ? new Date(form.scheduledFor).toISOString() : undefined,
      reportPresetId: form.reportPresetId || undefined,
    });
  };

  const formDisabled = !hasProperties || mutation.isPending || isLoadingProperties;

  const presetOptions = useMemo(() => {
    return presets.map((preset) => ({ id: preset._id, label: preset.name }));
  }, [presets]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="glass-card w-full max-w-2xl space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Create inspection</h3>
            <p className="text-sm text-muted-foreground">Select a property and optional preset to start a new inspection.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="focus-ring rounded-full border border-white/20 px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>
        {!hasProperties && !isLoadingProperties && (
          <div className="flex flex-col gap-2 rounded-2xl border border-warning/40 bg-warning/10 px-3 py-3 text-sm text-warning">
            <p className="font-medium">Add a property first before creating inspections.</p>
            {onRequestCreateProperty ? (
              <button
                type="button"
                onClick={onRequestCreateProperty}
                className="focus-ring self-start rounded-full border border-warning/40 bg-white/80 px-3 py-1 text-xs font-semibold text-warning transition hover:bg-white"
              >
                Go to properties
              </button>
            ) : null}
          </div>
        )}
        {isLoadingProperties && (
          <div className="rounded-2xl border border-border/40 bg-white/40 px-3 py-3 text-sm text-muted-foreground dark:bg-white/10">
            Loading properties…
          </div>
        )}
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
          <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Property
            <select
              value={form.propertyId}
              onChange={(event) => setForm((prev) => ({ ...prev, propertyId: event.target.value }))}
              className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              disabled={formDisabled || isLoadingProperties}
              required
            >
              <option value="" disabled>
                Select property…
              </option>
              {properties.map((property) => (
                <option key={property._id} value={property._id}>
                  {property.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Scheduled for (optional)
            <input
              type="date"
              value={form.scheduledFor}
              onChange={(event) => setForm((prev) => ({ ...prev, scheduledFor: event.target.value }))}
              className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              disabled={mutation.isPending}
            />
          </label>
          <label
            className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground md:col-span-2"
            data-onboarding="inspection-preset"
          >
            Report preset (optional)
            <select
              value={form.reportPresetId}
              onChange={(event) => setForm((prev) => ({ ...prev, reportPresetId: event.target.value }))}
              className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              disabled={mutation.isPending}
            >
              <option value="">Use organization default</option>
              {presetOptions.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
            {presets.length === 0 && (
              <p className="text-xs text-muted-foreground">No presets yet. You can create one in settings.</p>
            )}
          </label>

          {/* Info about AI room classification */}
          <div className="md:col-span-2 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm">
            <p className="font-medium text-primary">📸 AI-Powered Room Detection</p>
            <p className="mt-1 text-muted-foreground">
              After creating the inspection, upload your photos. Our AI will automatically classify them into rooms 
              (Kitchen, Living Room, Bathroom, etc.) and detect any issues.
            </p>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-glass btn-secondary focus-ring text-sm font-semibold"
              disabled={mutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={clsx(
                'btn-glass btn-primary focus-ring text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60',
                (!form.propertyId || mutation.isPending) && 'opacity-60'
              )}
              disabled={!form.propertyId || mutation.isPending}
            >
              {mutation.isPending ? 'Creating…' : 'Create inspection'}
            </button>
          </div>
          {error && <p className="md:col-span-2 text-sm text-[hsl(var(--destructive))]">{error}</p>}
        </form>
      </div>
    </div>
  );
}
